import { StakeholderAsset } from '../types/application';
import {
  APIDocumentCategory,
  APIDocumentSession,
  APIStakeholder,
  AddCompanyCategoryPayload,
  DocumentType,
  StakeholderFormValues,
} from '../types/documents';
import axios from './axios';

export function addCompanyShareholder(
  companyId: number,
  values: StakeholderFormValues,
  files: StakeholderAsset[],
  isAdmin: boolean,
  parentId?: number
): Promise<APIStakeholder> {
  const data = new FormData();
  if (parentId) {
    data.append('parent_id', parentId.toString());
  }
  data.append('person_type', values.personType);
  data.append('full_name', values.fullName);
  data.append('nationality', values.nationality);
  data.append('country_of_incorporation', values.countryOfIncorporation);
  data.append('state_of_incorporation', values.stateOfIncorporation);
  data.append('business_structure', values.businessStructure);
  data.append('date_of_incorporation', values.dateOfIncorporation);
  data.append('person_id_number', values.personIdNumber);
  if (values.percentageShare !== null && values.percentageShare !== undefined) {
    data.append('percentage_share', values.percentageShare.toString());
  }
  data.append('tax_id_type', values.taxIdType);
  data.append('date_of_birth', values.dateOfBirth);
  data.append('address', values.address);
  data.append('email', values.email);
  data.append('phone_number', values.phoneNumber);
  data.append('job_title', values.jobTitle);
  data.append('government_id_issued_in', values.governmentIdIssuedIn);
  data.append('government_id_type', values.governmentIdType);
  data.append('government_id_number', values.governmentIdNumber);
  data.append('address_country', values.addressCountry);
  data.append('address_state', values.addressState);
  data.append('address_city', values.addressCity);
  data.append('address_zip', values.addressZip);
  data.append('address_line_1', values.addressLine1);
  data.append('address_line_2', values.addressLine2);
  data.append('is_controlling_person', values.isControllingPerson ? 'true' : 'false');
  files.forEach(({ file, assetType }) => {
    data.append('asset', file, file.name);
    data.append('file_name', file.name);
    data.append('asset_type', assetType);
  });
  const response = axios.post<Partial<APIStakeholder>, APIStakeholder>(
    `${isAdmin ? '/admins' : ''}/document_collection/companies/${companyId}/shareholders/`,
    data
  );
  return response;
}

export function uploadStakeholderAssets(
  companyId: number,
  shareholderId: number,
  files: StakeholderAsset[],
  isAdmin: boolean,
  stakeholderType: 'shareholders' | 'legal_representatives'
): Promise<APIStakeholder> {
  const data = new FormData();
  files.forEach(({ file, assetType }) => {
    data.append('asset', file, file.name);
    data.append('file_name', file.name);
    data.append('asset_type', assetType);
  });
  return axios
    .post(
      `${
        isAdmin ? '/admins' : ''
      }/document_collection/companies/${companyId}/${stakeholderType}/${shareholderId}/assets/`,
      data
    )
    .then(({ data }) => data);
}

export function updateShareholderAsset(
  companyId: number,
  shareholderId: number,
  assetId: number,
  values: any,
  isAdmin: boolean = false
): Promise<any> {
  return axios
    .patch(
      `${
        isAdmin ? '/admins' : ''
      }/document_collection/companies/${companyId}/shareholders/${shareholderId}/assets/${assetId}/`,
      values
    )
    .then(({ data }) => data);
}

export function deleteDocumentAsset(
  companyId: number,
  documentId: number,
  assetId: number,
  isAdmin: boolean = false
): Promise<any> {
  return axios
    .delete(
      `${
        isAdmin ? '/admins' : ''
      }/document_collection/companies/${companyId}/documents/${documentId}/assets/${assetId}/`
    )
    .then(({ data }) => data);
}

export function editCompanyShareholder(
  companyId: number,
  shareholderId: number,
  values: StakeholderFormValues,
  isAdmin: boolean
): Promise<any> {
  return axios
    .patch(
      `${isAdmin ? '/admins' : ''}/document_collection/companies/${companyId}/shareholders/${shareholderId}/`,
      values
    )
    .then(({ data }) => data);
}

export function deleteCompanyShareholder(
  companyId: number,
  shareholderId: number,
  isAdmin: boolean
): Promise<any> {
  return axios
    .delete(
      `${isAdmin ? '/admins' : ''}/document_collection/companies/${companyId}/shareholders/${shareholderId}/`
    )
    .then(({ data }) => data);
}

export function addCompanyLegalRepresentative(
  companyId: number,
  values: StakeholderFormValues,
  files: StakeholderAsset[],
  isAdmin: boolean
): Promise<APIStakeholder> {
  const data = new FormData();
  data.append('full_name', values.fullName);
  data.append('email', values.email);
  data.append('person_id_number', values.personIdNumber);
  data.append('person_type', values.personType);
  data.append('nationality', values.nationality);
  data.append('tax_id_type', values.taxIdType);
  data.append('date_of_birth', values.dateOfBirth);
  data.append('address', values.address);
  data.append('email', values.email);
  data.append('phone_number', values.phoneNumber);
  data.append('job_title', values.jobTitle);
  data.append('government_id_issued_in', values.governmentIdIssuedIn);
  data.append('government_id_type', values.governmentIdType);
  data.append('government_id_number', values.governmentIdNumber);
  data.append('address_country', values.addressCountry);
  data.append('address_state', values.addressState);
  data.append('address_city', values.addressCity);
  data.append('address_zip', values.addressZip);
  data.append('address_line_1', values.addressLine1);
  data.append('address_line_2', values.addressLine2);
  files.forEach(({ file, assetType }) => {
    data.append('asset', file, file.name);
    data.append('file_name', file.name);
    data.append('asset_type', assetType);
  });
  const response = axios.post<Partial<APIStakeholder>, APIStakeholder>(
    `${isAdmin ? '/admins' : ''}/document_collection/companies/${companyId}/legal_representatives/`,
    data
  );
  return response;
}

export function editCompanyLegalRepresentative(
  companyId: number,
  legalRepresentativeId: number,
  values: StakeholderFormValues,
  isAdmin: boolean
): Promise<any> {
  return axios
    .patch(
      `${
        isAdmin ? '/admins' : ''
      }/document_collection/companies/${companyId}/legal_representatives/${legalRepresentativeId}/`,
      values
    )
    .then(({ data }) => data);
}

export function deleteCompanyLegalRepresentative(
  companyId: number,
  legalRepresentativeId: number,
  isAdmin: boolean
): Promise<any> {
  return axios
    .delete(
      `${
        isAdmin ? '/admins' : ''
      }/document_collection/companies/${companyId}/legal_representatives/${legalRepresentativeId}/`
    )
    .then(({ data }) => data);
}

export function uploadLegalRepresentativeAssets(
  companyId: number,
  legalRepresentativeId: number,
  files: StakeholderAsset[],
  isAdmin: boolean
): Promise<APIStakeholder> {
  const data = new FormData();
  files.forEach(({ file, assetType }) => {
    data.append('asset', file, file.name);
    data.append('file_name', file.name);
    data.append('asset_type', assetType);
  });
  return axios
    .post(
      `${
        isAdmin ? '/admins' : ''
      }/document_collection/companies/${companyId}/legal_representatives/${legalRepresentativeId}/assets/`,
      data
    )
    .then(({ data }) => data);
}

export function updateLegalRepresentativeAsset(
  companyId: number,
  legalRepresentativeId: number,
  assetId: number,
  values: any,
  isAdmin: boolean = false
): Promise<any> {
  return axios
    .patch(
      `${
        isAdmin ? '/admins' : ''
      }/document_collection/companies/${companyId}/legal_representatives/${legalRepresentativeId}/assets/${assetId}/`,
      values
    )
    .then(({ data }) => data);
}

export function getCompanyShareholders(
  companyId: number,
  includeDescendants = false
): Promise<APIStakeholder[]> {
  return axios
    .get(`/document_collection/companies/${companyId}/shareholders/`, {
      params: { include_descendants: includeDescendants },
    })
    .then(({ data }) => data);
}

export function getCompanyLegalRepresentatives(companyId: number): Promise<APIStakeholder[]> {
  return axios
    .get(`/document_collection/companies/${companyId}/legal_representatives/`)
    .then(({ data }) => data);
}

export function getCompanyDocumentCategories(companyId: number): Promise<APIDocumentCategory[]> {
  return axios.get(`/document_collection/companies/${companyId}/documents/`).then(({ data }) => data);
}

export function adminGetCompanyDocumentCategories(companyId: number): Promise<APIDocumentCategory[]> {
  return axios.get(`/admins/document_collection/companies/${companyId}/documents/`).then(({ data }) => data);
}

export function adminNotifyUserCompanyDocument(companyId: number, documentId: number) {
  return axios
    .post(`/admins/document_collection/companies/${companyId}/documents/${documentId}/notify/`)
    .then(({ data }) => data);
}

export function adminGetCompanyShareholders(
  companyId: number,
  includeDescendants = false
): Promise<APIStakeholder[]> {
  return axios
    .get(`/admins/document_collection/companies/${companyId}/shareholders/`, {
      params: { include_descendants: includeDescendants },
    })
    .then(({ data }) => data);
}

export function adminGetCompanyLegalRepresentatives(companyId: number): Promise<APIStakeholder[]> {
  return axios
    .get(`/admins/document_collection/companies/${companyId}/legal_representatives/`)
    .then(({ data }) => data);
}

export function adminToggleCompanyDocumentCategory(
  companyId: number,
  categoryId: number
): Promise<APIDocumentCategory> {
  return axios
    .post(`/admins/document_collection/companies/${companyId}/categories/${categoryId}/toggle/`)
    .then(({ data }) => data);
}

export function adminAcceptCompanyDocumentCategory(
  companyId: number,
  categoryId: number
): Promise<APIDocumentCategory> {
  return axios
    .post(`/admins/document_collection/companies/${companyId}/categories/${categoryId}/accept/`)
    .then(({ data }) => data);
}

export function adminRejectCompanyDocumentCategory(
  companyId: number,
  categoryId: number,
  rejectionComment: string
): Promise<APIDocumentCategory> {
  return axios
    .post(`/admins/document_collection/companies/${companyId}/categories/${categoryId}/reject/`, {
      rejectedComment: rejectionComment,
    })
    .then(({ data }) => data);
}

export function adminCreateCompanyDocumentCategory(
  companyId: number,
  payload: AddCompanyCategoryPayload
): Promise<APIDocumentCategory> {
  return axios
    .post(`/admins/document_collection/companies/${companyId}/categories/`, { ...payload, enabled: true })
    .then(({ data }) => data);
}

export function uploadDocumentAssets(
  companyId: number,
  documentId: number,
  data: FormData,
  isAdmin: boolean = false
): Promise<APIDocumentSession> {
  return axios
    .post(
      `${
        isAdmin ? '/admins' : ''
      }/document_collection/companies/${companyId}/documents/${documentId}/assets/`,
      data,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    .then(({ data }) => data);
}

export function downloadDocumentAsset(
  companyId: number,
  documentId: number,
  assetId: number,
  isAdmin: boolean = false
) {
  return axios
    .get(
      `${
        isAdmin ? '/admins' : ''
      }/document_collection/companies/${companyId}/documents/${documentId}/assets/${assetId}/`,
      { responseType: 'blob' }
    )
    .then((response) => response);
}

export function downloadDocumentAssetsByDocumentType(
  companyId: number,
  documentType: DocumentType,
  isAdmin: boolean = false
) {
  return axios
    .get(
      `${
        isAdmin ? '/admins' : ''
      }/document_collection/companies/${companyId}/download_assets_by_type/?document_type=${documentType}`,
      { responseType: 'blob' }
    )
    .then(({ data }) => data);
}

export function downloadDocumentAssetsByDocumentId(companyId: number, documentId: number) {
  return axios
    .get(`/document_collection/companies/${companyId}/documents/${documentId}/download/`, {
      responseType: 'blob',
    })
    .then(({ data }) => data);
}

export function downloadAllDocuments(companyId: number): Promise<any> {
  return axios
    .get(`/admins/document_collection/companies/${companyId}/download_documents/`)
    .then(({ data }) => data);
}
